import {
    SESSION_KEY_FIREBASE_LOGIN_METHOD,
    SESSION_KEY_FIREBASE_LOGIN_PENDING,
    SESSION_KEY_FIREBASE_LOGIN_RETURN_TO,
    SESSION_KEY_FIREBASE_LOGIN_TRIGGER_SCENE,
    STORAGE_KEY_FIREBASE_LOGIN_DYE_PARAMS,
} from '@/constants/storage';
import { reportLoginAttempt } from '@/utils/arms/login-events';
import { pickLandingPersistParams, withDyeQueryFromRecord } from '@/utils/router/with-dye-query';
import { localStore, sessionStore } from '@/utils/storage';

export interface IFirebaseOAuthRedirectTracking {
    trigger_scene: string;
    login_method: string;
}

export interface IMarkOAuthRedirectStartOptions {
    /** Facebook 移动端在 loginWithFacebook 入口已上报 attempt 时传 true，避免重复 */
    skipLoginAttempt?: boolean;
}

export function readLoginPending(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    try {
        return window.sessionStorage.getItem(SESSION_KEY_FIREBASE_LOGIN_PENDING) === '1';
    } catch {
        return false;
    }
}

/**
 * signInWithRedirect 前写入：回跳后用于展示 loading 与成功后的 replace 目标（仅可信站内路径）。
 * @param returnTo 以 / 开头的 pathname + search，如 /read/1?c=2；登录全屏页可传 /my
 * @param tracking 与 LOGIN_CLICK 对齐，供回跳后 LOGIN_RESULT（回跳后 URL 未必含 trigger_scene）
 */
export function markOAuthRedirectStart(
    returnTo: string,
    tracking?: IFirebaseOAuthRedirectTracking,
    options?: IMarkOAuthRedirectStartOptions
): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.sessionStorage.setItem(SESSION_KEY_FIREBASE_LOGIN_PENDING, '1');
        window.sessionStorage.setItem(SESSION_KEY_FIREBASE_LOGIN_RETURN_TO, returnTo);
        window.sessionStorage.setItem(SESSION_KEY_FIREBASE_LOGIN_TRIGGER_SCENE, tracking?.trigger_scene ?? '');
        // 持久化缓存，用于跨会话埋点归因等
        window.localStorage.setItem(SESSION_KEY_FIREBASE_LOGIN_METHOD, tracking?.login_method ?? '');
        // 快照落地五参（localStorage：Facebook 移动端常在另一 tab 回 /login，session 不共享）
        const dyeParams = pickLandingPersistParams();
        if (Object.keys(dyeParams).length > 0) {
            localStore.setItem(STORAGE_KEY_FIREBASE_LOGIN_DYE_PARAMS, JSON.stringify(dyeParams));
        } else {
            localStore.removeItem(STORAGE_KEY_FIREBASE_LOGIN_DYE_PARAMS);
        }
        sessionStore.removeItem(STORAGE_KEY_FIREBASE_LOGIN_DYE_PARAMS);
        if (tracking?.login_method && !options?.skipLoginAttempt) {
            reportLoginAttempt({
                login_method: tracking.login_method,
                trigger_scene: tracking.trigger_scene,
            });
        }
    } catch {
        // ignore
    }
}

const EMPTY_OAUTH_TRACKING: IFirebaseOAuthRedirectTracking = {
    trigger_scene: '',
    login_method: '',
};

export function readOAuthLoginTrackingContext(): IFirebaseOAuthRedirectTracking {
    if (typeof window === 'undefined') {
        return { ...EMPTY_OAUTH_TRACKING };
    }
    try {
        return {
            trigger_scene: window.sessionStorage.getItem(SESSION_KEY_FIREBASE_LOGIN_TRIGGER_SCENE) ?? '',
            login_method: window.localStorage.getItem(SESSION_KEY_FIREBASE_LOGIN_METHOD) ?? '',
        };
    } catch {
        return { ...EMPTY_OAUTH_TRACKING };
    }
}

/** 登出时清除 localStorage login method */
export function clearLoginMethod(): void {
    localStore.removeItem(SESSION_KEY_FIREBASE_LOGIN_METHOD);
}

export function readReturnToForOAuth(): string | null {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        return window.sessionStorage.getItem(SESSION_KEY_FIREBASE_LOGIN_RETURN_TO);
    } catch {
        return null;
    }
}

function clearReturnTo(): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.sessionStorage.removeItem(SESSION_KEY_FIREBASE_LOGIN_RETURN_TO);
        window.sessionStorage.removeItem(SESSION_KEY_FIREBASE_LOGIN_TRIGGER_SCENE);
        localStore.removeItem(STORAGE_KEY_FIREBASE_LOGIN_DYE_PARAMS);
        sessionStore.removeItem(STORAGE_KEY_FIREBASE_LOGIN_DYE_PARAMS);
    } catch {
        // ignore
    }
}

/** 读取 OAuth 跳走前快照的落地五参（不清除）；Facebook 等长异步前应尽早读入内存 */
export function readOAuthLoginDyeParams(): Record<string, string> | null {
    try {
        const raw =
            localStore.getItem(STORAGE_KEY_FIREBASE_LOGIN_DYE_PARAMS) ??
            sessionStore.getItem(STORAGE_KEY_FIREBASE_LOGIN_DYE_PARAMS);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return null;
        }
        const normalized = pickLandingPersistParams(new URLSearchParams(parsed as Record<string, string>));
        return Object.keys(normalized).length > 0 ? normalized : null;
    } catch {
        return null;
    }
}

/**
 * OAuth 整页回跳后，将登录前快照的落地五参合并进目标路径后执行 replace。
 * @param dyeParams 可选；传入则不再读 storage（避免异步登录期间被其他 handler 清掉）
 */
export function replaceWithOAuthDyeRestore(
    path: string,
    replace: (to: string) => void,
    dyeParams?: Record<string, string> | null
): void {
    replace(withDyeQueryFromRecord(path, dyeParams !== undefined ? dyeParams : readOAuthLoginDyeParams()));
}

export function clearLoginPending(): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.sessionStorage.removeItem(SESSION_KEY_FIREBASE_LOGIN_PENDING);
    } catch {
        // ignore
    }
    clearReturnTo();
}

/**
 * 当前地址，用于从弹窗发起 OAuth 成功后回到原页。
 */
export function getOAuthReturnToForCurrentPage(): string {
    if (typeof window === 'undefined') {
        return '/profile';
    }
    return `${window.location.pathname}${window.location.search}`;
}

/** 登录全屏页成功后去「我的」 */
export const OAUTH_RETURN_TO_LOGIN_PAGE = '/profile' as const;

/**
 * 防止开放重定向：只接受以单 / 开头的站内相对路径（含 query）。
 */
export function sanitizeReturnToForReplace(raw: string | null, fallback: string = '/profile'): string {
    if (raw == null || raw.length === 0) {
        return fallback;
    }
    const t = raw.trim();
    if (!t.startsWith('/') || t.startsWith('//') || t.includes('://')) {
        return fallback;
    }
    return t;
}
